// The in-memory API's outbound webhooks (M1-07; services/api/src/routes/webhooks.ts), with the
// real routes' rules: managers only (404 outside the company, 403 for a viewer), https URLs only,
// at least one event type, at most 25 hooks, the signing secret (`whsec_…`) only in the create
// and rotate responses, a test send that records a delivery, deliveries newest first, and a
// manual retry for failed / abandoned deliveries only (409 otherwise). Nothing leaves the
// process: a URL whose host contains "fail" answers 503, every other URL answers 200.
// WOS-BUNDLE: a hook paused after five events in a row exhausted their retries (Beta has one,
// seeded), `webhooks.resume` re-arms it, and `filter.container` scopes it to a space or a board.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, page, problem, validation } from "./fake";
import { COMPANY_IDS, uid } from "./fake-data";

type Hook = OperationOutput<"webhooks.list">["data"][number];
type Delivery = OperationOutput<"webhooks.deliveries">["data"][number];

const MAX_WEBHOOKS = 25;
const forbidden = () => problem(403, "forbidden", "Forbidden", "managers only");
const companyNo = (id: string) => Number.parseInt(id.slice(-4), 16) % 4096;

export function createWebhookHandlers(deps: {
  now: () => Date;
  /** Every event type of the catalog (a body naming another is 422). */
  eventTypes: () => readonly string[];
  /** WOS-BUNDLE: the name of the company's space or board, or null when it has none by that id. */
  containerOf?: (companyId: string, kind: "space" | "board", id: string) => string | null;
}): Partial<Record<RouteId, Handler>> {
  const hooks = new Map<string, Hook[]>();
  const deliveries = new Map<string, Delivery[]>();
  let seq = 0;
  const at = () => deps.now().toISOString();
  const hooksOf = (co: string) => {
    let l = hooks.get(co);
    if (!l) {
      l = co === COMPANY_IDS.beta ? [pausedSeed(co)] : [];
      hooks.set(co, l);
    }
    return l;
  };
  /** Beta's hook to a dead endpoint: five events in a row failed every retry, so it paused. */
  function pausedSeed(co: string): Hook {
    const t = new Date(deps.now().getTime() - 3 * 3_600_000).toISOString();
    const hook: Hook = {
      company_id: co,
      id: uid(0x7c00, 0xbe0001),
      url: "https://hooks.fail.example/companyos",
      description: "Old CRM sync",
      types: ["task.created", "task.status_changed"],
      filter: {},
      status: "paused",
      disabled_at: null,
      disabled_reason: null,
      consecutive_failures: 5,
      paused_at: t,
      actor_kind: "person",
      actor_membership_id: null,
      created_at: new Date(deps.now().getTime() - 20 * 86_400_000).toISOString(),
      updated_at: t,
    };
    for (let i = 0; i < 5; i++) record(hook, "task.created", false, 503);
    return hook;
  }
  const deliveriesOf = (id: string) => {
    const l = deliveries.get(id) ?? [];
    deliveries.set(id, l);
    return l;
  };
  const secret = () => `whsec_fake${String(++seq).padStart(4, "0")}${"x".repeat(32)}`;
  const answers = (url: string) => (new URL(url).hostname.includes("fail") ? 503 : 200);

  function badBody(b: Record<string, unknown>, partial: boolean): string | null {
    if (!partial || b.url !== undefined) {
      if (typeof b.url !== "string" || !/^https:\/\/\S+$/i.test(b.url))
        return "url: must start with https://";
    }
    if (!partial || b.types !== undefined) {
      if (!Array.isArray(b.types) || !b.types.length) return "types: pick at least one event type";
      const known = new Set(deps.eventTypes());
      const bad = b.types.filter((t) => !known.has(t as string));
      if (bad.length) return `types: unknown event types: ${bad.join(", ")}`;
    }
    if (b.filter !== undefined) {
      const f = b.filter as { container?: { kind?: unknown; id?: unknown } } | null;
      const c = f?.container;
      if (c !== undefined && c.kind !== "space" && c.kind !== "board")
        return "filter.container.kind: space or board";
    }
    return null;
  }

  /** WOS-BUNDLE: a scope names a space or board of the company (else 404, as the API). */
  function scopeMissing(co: string, filter: unknown): boolean {
    const c = (filter as { container?: { kind: "space" | "board"; id: string } } | null)?.container;
    return Boolean(c && deps.containerOf && deps.containerOf(co, c.kind, String(c.id)) === null);
  }

  function record(h: Hook, eventType: string, ok: boolean, status: number): Delivery {
    const t = at();
    const d: Delivery = {
      company_id: h.company_id,
      id: uid(0x7a00, ++seq + companyNo(h.company_id) * 1000),
      webhook_id: h.id,
      event_id: uid(0x7b00, seq),
      event_occurred_at: t,
      event_type: eventType,
      status: ok ? "succeeded" : "failed",
      attempts: 1,
      response_status: status,
      error: ok ? null : `HTTP ${status}`,
      next_attempt_at: null,
      delivered_at: ok ? t : null,
      created_at: t,
      updated_at: t,
    };
    deliveriesOf(h.id).push(d);
    return d;
  }

  return {
    "webhooks.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      return json(page(hooksOf(p.company.id), url));
    },
    "webhooks.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const b = (body ?? {}) as Record<string, unknown>;
      const bad = badBody(b, false);
      if (bad) return validation(bad);
      if (hooksOf(p.company.id).length >= MAX_WEBHOOKS) return validation("url: limit reached");
      if (scopeMissing(p.company.id, b.filter)) return notFound();
      const t = at();
      const hook: Hook = {
        consecutive_failures: 0,
        paused_at: null,
        company_id: p.company.id,
        id: uid(0x7c00, ++seq + companyNo(p.company.id) * 1000),
        url: b.url as string,
        description: typeof b.description === "string" ? b.description : null,
        types: [...new Set(b.types as string[])],
        filter: (b.filter as Hook["filter"] | undefined) ?? {},
        status: "active",
        disabled_at: null,
        disabled_reason: null,
        actor_kind: p.operator ? "agency_user" : "person",
        actor_membership_id: null,
        created_at: t,
        updated_at: t,
      };
      hooksOf(p.company.id).push(hook);
      return json({ webhook: hook, secret: secret() }, 201);
    },
    "webhooks.get": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const h = hooksOf(p.company.id).find((x) => x.id === params.id);
      return h ? json(h) : notFound();
    },
    "webhooks.update": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const h = hooksOf(p.company.id).find((x) => x.id === params.id);
      if (!h) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const bad = badBody(b, true);
      if (bad) return validation(bad);
      if (typeof b.url === "string") h.url = b.url;
      if (b.description !== undefined)
        h.description = typeof b.description === "string" ? b.description : null;
      if (Array.isArray(b.types)) h.types = [...new Set(b.types as string[])];
      if (b.filter !== undefined) {
        if (scopeMissing(p.company.id, b.filter)) return notFound();
        h.filter = (b.filter as Hook["filter"] | null) ?? {};
      }
      if (b.status === "active") {
        h.status = "active";
        h.disabled_at = null;
        h.disabled_reason = null;
        h.consecutive_failures = 0;
        h.paused_at = null;
      } else if (b.status === "disabled") {
        h.status = "disabled";
        h.disabled_at = at();
        h.disabled_reason = "disabled by a person";
      }
      h.updated_at = at();
      return json(h);
    },
    // WOS-BUNDLE: re-arm a paused (or disabled) hook; its failure count starts again at 0.
    "webhooks.resume": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const h = hooksOf(p.company.id).find((x) => x.id === params.id);
      if (!h) return notFound();
      h.status = "active";
      h.disabled_at = null;
      h.disabled_reason = null;
      h.consecutive_failures = 0;
      h.paused_at = null;
      h.updated_at = at();
      return json(h);
    },
    "webhooks.delete": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const list = hooksOf(p.company.id);
      const i = list.findIndex((x) => x.id === params.id);
      if (i < 0) return notFound();
      list.splice(i, 1);
      deliveries.delete(params.id as string);
      return new Response(null, { status: 204 });
    },
    "webhooks.test": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const h = hooksOf(p.company.id).find((x) => x.id === params.id);
      if (!h) return notFound();
      const status = answers(h.url);
      const d = record(h, "webhook.tested", status === 200, status);
      return json({
        delivery_id: d.id,
        ok: d.status === "succeeded",
        response_status: status,
        error: d.error,
      });
    },
    "webhooks.rotateSecret": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const h = hooksOf(p.company.id).find((x) => x.id === params.id);
      if (!h) return notFound();
      h.updated_at = at();
      return json({ webhook: h, secret: secret() });
    },
    "webhooks.deliveries": ({ principal: p, params, url }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const h = hooksOf(p.company.id).find((x) => x.id === params.id);
      if (!h) return notFound();
      return json(page([...deliveriesOf(h.id)].reverse(), url));
    },
    "webhooks.retryDelivery": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const h = hooksOf(p.company.id).find((x) => x.id === params.id);
      const d = h && deliveriesOf(h.id).find((x) => x.id === params.delivery_id);
      if (!h || !d) return notFound();
      if (d.status === "pending" || d.status === "succeeded")
        return problem(409, "conflict", "Conflict", "the delivery cannot be retried now");
      const status = answers(h.url);
      d.attempts += 1;
      d.response_status = status;
      d.status = status === 200 ? "succeeded" : "failed";
      d.error = status === 200 ? null : `HTTP ${status}`;
      d.delivered_at = status === 200 ? at() : d.delivered_at;
      d.updated_at = at();
      return json(d);
    },
  };
}
